import React, { useEffect, useRef, useState } from "react";
import ToolShell from "@/components/ToolShell";
import Spinner from "@/components/Spinner";
import { Send, Sparkles } from "lucide-react";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "What's the difference between subsidized and unsubsidized loans?",
  "My parents are divorced, whose info goes on the FAFSA?",
  "I missed my state deadline. What should I do now?",
  "How do I appeal a financial aid offer?",
];

export default function ChatPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { track.tool_opened("chat"); }, []);
  useEffect(() => {
    if (messages.length === 0 && !streaming) return;
    scrollRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, streaming]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || streaming) return;
    track.chat_message_sent();
    const nextMsgs = [...messages, { role: "user", content: text }];
    setMessages(nextMsgs);
    setInput("");
    setStreaming(true);
    // Start an empty assistant bubble
    setMessages([...nextMsgs, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: nextMsgs }),
      });
      if (!res.ok || !res.body) throw new Error("Bad response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        setMessages(prev => {
          const next = prev.slice();
          next[next.length - 1] = { role: "assistant", content: acc };
          return next;
        });
      }
    } catch {
      setMessages(prev => {
        const next = prev.slice();
        next[next.length - 1] = { role: "assistant", content: "I'm having trouble right now, try again in a moment." };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <ToolShell tool="Tool 01 · AI Chat" title="Ask a question." subtitle="Direct answers on FAFSA, aid eligibility, deadlines, and appeals. Nothing you type here is saved, this conversation lives only in your browser tab.">
      <div className="card min-h-[60vh] flex flex-col" data-testid="chat-container">
        <div className="flex-1 overflow-y-auto pr-2 space-y-5">
          {messages.length === 0 && (
            <div className="flex flex-col items-start gap-4 py-8" data-testid="chat-empty">
              <div className="text-ink-muted text-sm flex items-center gap-2"><Sparkles className="w-4 h-4 text-accent" /> Try one of these to start:</div>
              <div className="grid sm:grid-cols-2 gap-2 w-full">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => { track.chat_suggestion_clicked(); send(s); }} className="text-left text-sm p-3 rounded-lg border border-border hover:border-border-hover hover:bg-white/5 transition-colors" data-testid={`chat-suggestion-${i}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`fade-up ${m.role === "user" ? "flex justify-end" : ""}`}>
              {m.role === "user" ? (
                <div className="msg-user max-w-[80%]" data-testid={`chat-msg-user-${i}`}>{m.content}</div>
              ) : (
                <div className="msg-assistant max-w-[85%] whitespace-pre-wrap" data-testid={`chat-msg-asst-${i}`}>
                  {m.content || (
                    <Spinner variant="inline" label="Thinking" testId={`chat-thinking-${i}`} />
                  )}
                </div>
              )}
            </div>
          ))}
          <div ref={scrollRef} />
        </div>
        <div className="pt-4 mt-4 border-t border-border flex gap-3">
          <input className="input flex-1" value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
            placeholder="Ask anything about FAFSA, aid, or deadlines…"
            disabled={streaming} data-testid="chat-input" />
          <button onClick={() => send()} disabled={streaming || !input.trim()} className="btn btn-primary" data-testid="chat-send">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
      <p className="text-xs text-ink-dim mt-4" data-testid="chat-privacy-note">
        AidWaive doesn't store chat history. This conversation disappears when you close the tab.
      </p>
    </ToolShell>
  );
}
